import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { TransactionMonitoringModel } from './entity-transaction-monitoring.model';
import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';

import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { plainToClass } from 'class-transformer';
import { UpdateTransactionStatus } from './update-transaction-status/update-transaction-model';

/**
 * Service for Transaction Monitoring.
 */
@Injectable()
export class EntityTransactionMonitoringService {

    private entityIdDetails: EntityIdDetails;
    private broadcastLoadTransationRecords = new BehaviorSubject<boolean>(false);
    flagToLoadTransactionRecords = this.broadcastLoadTransationRecords.asObservable();

    constructor(private requestService: ObservableRequestService) { }

    getTransactionDetails(clientId: number, pageNumber: number, status: any, bankAccount?: string, searchText?: string): Observable<any> {
        let transactionDetailsUrl = ConstantsService.TRANSACTION + '?clientId=' + clientId + '&size=10&pageNumber='
         + pageNumber;
        if (status) {
            // Handle multiple status values - JAX-RS accepts multiple parameters with same name
            if (Array.isArray(status) && status.length > 0) {
                // Send multiple status parameters: status=1&status=2&status=3
                status.forEach(statusId => {
                    if (statusId) {
                        transactionDetailsUrl += '&status=' + encodeURIComponent(statusId);
                    }
                });
            } else if (typeof status === 'string' && status.includes(',')) {
                // If it's a comma-separated string, split and send as multiple parameters
                const statusArray = status.split(',');
                statusArray.forEach(statusId => {
                    if (statusId.trim()) {
                        transactionDetailsUrl += '&status=' + encodeURIComponent(statusId.trim());
                    }
                });
            } else {
                // Single status value
                transactionDetailsUrl += '&status=' + encodeURIComponent(status);
            }
        }
        if (bankAccount && bankAccount.trim()) {
            transactionDetailsUrl += '&accountNo=' + encodeURIComponent(bankAccount.trim());
        }
        if (searchText && searchText.trim()) {
            transactionDetailsUrl += '&searchText=' + encodeURIComponent(searchText.trim());
        }
        return this.requestService.getRequest(transactionDetailsUrl);
        };

    updateTransactionsStatus(updateTransactionStatus: UpdateTransactionStatus) {
        return this.requestService.putRequest(ConstantsService.TRANSACTION_STATUS,
            JSON.stringify(updateTransactionStatus),
            this.requestService.getJSONContentType());
    }

    sendBroadCastToLoadTransactionRecords(flag: boolean) {
        this.broadcastLoadTransationRecords.next(flag);
    }

    runRulesForPendingTransactions(clientId: number): Observable<any> {
        const url = ConstantsService.RUN_RULES_FOR_PENDING_TRANSACTIONS + '?clientId=' + clientId;
        return this.requestService.postRequest(url, {}, this.requestService.getJSONContentType());
    }

    checkNameMatch(name1: string, name2: string): Observable<any> {
        const url = ConstantsService.CHECK_NAME_MATCH + '?name1=' + encodeURIComponent(name1) + '&name2=' + encodeURIComponent(name2);
        return this.requestService.getRequest(url);
    }

    testTransactionRuleMatch(testData: any): Observable<any> {
        const url = ConstantsService.TEST_TRANSACTION_RULE_MATCH;
        return this.requestService.postRequest(url, JSON.stringify(testData), this.requestService.getJSONContentType());
    }

  /**
   * Search transactions for a client within an optional date range.
   * Used by the transaction profile UI to select a transaction to test against a rule.
   */
  searchTransactions(clientId: number, fromDate?: string, toDate?: string, maxResults: number = 200): Observable<any> {
      let url = ConstantsService.SEARCH_TRANSACTIONS + '?clientId=' + clientId;
      if (fromDate) {
          url += '&fromDate=' + encodeURIComponent(fromDate);
      }
      if (toDate) {
          url += '&toDate=' + encodeURIComponent(toDate);
      }
      if (maxResults) {
          url += '&maxResults=' + maxResults;
      }
      return this.requestService.getRequest(url);
  }

    createTransactionProfileFromRecord(createData: any): Observable<any> {
        const url = ConstantsService.CREATE_TRANSACTION_PROFILE_FROM_RECORD;
        return this.requestService.postRequest(url, JSON.stringify(createData), this.requestService.getJSONContentType());
    }

    cleanupTransactionRecords(transactionIds: string[]): Observable<any> {
        const url = ConstantsService.CLEANUP_TRANSACTION_RECORDS;
        return this.requestService.postRequest(url, JSON.stringify(transactionIds), this.requestService.getJSONContentType());
    }

    escalateTransaction(transactionId: string, clientId: number): Observable<any> {
        const url = ConstantsService.ESCALATE_TRANSACTION + '?transactionId=' + encodeURIComponent(transactionId) + '&clientId=' + clientId;
        return this.requestService.postRequest(url, {}, this.requestService.getJSONContentType());
    }

}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-monitoring/entity-transaction-monitoring.service.ts